import { Navigate } from 'react-router-dom';

import { PermissionTypes, ResourceNameEnum } from '../../../constants';
import { useAppSelector } from '../../../hooks/reduxStoreHooks';
import useCheckUserIfLoggedUserIsLicensed from '../../../hooks/useCheckUserIfLoggedUserIsLicensed';
import useGetTypeOfPermissionForResource from '../../../hooks/useGetTypeOfPermissionForResource';
import useIsUserFromAdminOrDevCompany from '../../../hooks/useIsUserFromAdminOrDevCompany';
import {
  AccountSettingsPage,
  AddPlots,
  CompanyDocuments,
  CompanyManagementPage,
  CountersPage,
  CreateCompany,
  CreateLegality,
  CreateUser,
  DdsPage,
  EditCompany,
  EditDds,
  EditUser,
  HelpPage,
  ImportCadastralDataSerbia,
  LegalityPage,
  Page404,
  PlotDocuments,
  PlotsPage,
  SelectedCompanyPage,
  SubmitDds,
  UpdatePasswordPage,
  UsersManagementPage,
} from '../../../routes/loadableRoutes';
import {
  PATH_APP_ACCOUNT_SETTINGS,
  PATH_APP_COUNTERS,
  PATH_APP_DDS,
  PATH_APP_GEO_JSON_SERBIA,
  PATH_APP_HELP,
  PATH_APP_LEGALITY,
  PATH_APP_PLOTS,
  PATH_APP_PLOTS_MANAGEMENT,
  PATH_APP_SELECT_COMPANY,
  PATH_APP_UPDATE_PASSWORD,
  PATH_APP_USER_MANAGEMENT,
} from '../../../routes/paths';

export default function useGetAppRoutes() {
  const rootCompanyId = useAppSelector((state) => state.user.rootCompany?.id);

  const isFromAdminOrDevCompany = useIsUserFromAdminOrDevCompany();
  const isLicensedAdmin = useCheckUserIfLoggedUserIsLicensed({
    onlyAdmin: true,
  });

  const hasFullAccessToPlots = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.PLOT,
    permissionType: PermissionTypes.FULL,
  });

  const hasFulLAccessToLegality = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.LEGALITY,
    permissionType: PermissionTypes.FULL,
  });

  const hasViewAccessToLegality = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.LEGALITY,
    permissionType: PermissionTypes.VIEW,
  });

  const hasFullAccessToCompanies = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.COMPANY,
    permissionType: PermissionTypes.FULL,
  });

  const hasFullAccessToUsers = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.USER,
    permissionType: PermissionTypes.FULL,
  });

  const hasNoAccessToPlots = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.PLOT,
    permissionType: PermissionTypes.NO_ACCESS,
  });

  const hasNoAccessToCompanies = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.COMPANY,
    permissionType: PermissionTypes.NO_ACCESS,
  });

  const hasNoAccessToUsers = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.USER,
    permissionType: PermissionTypes.NO_ACCESS,
  });

  const hasNoAccessToLegality = useGetTypeOfPermissionForResource({
    resource: ResourceNameEnum.LEGALITY,
    permissionType: PermissionTypes.NO_ACCESS,
  });

  // Plot routes
  let plotRoutes = [
    { path: PATH_APP_PLOTS.root, element: <PlotsPage /> },
    {
      element: <Navigate to={PATH_APP_PLOTS.root} replace />,
      index: true,
    },
  ];

  if (hasFullAccessToPlots) {
    plotRoutes.push({
      path: `${PATH_APP_PLOTS_MANAGEMENT.newPlot}`,
      element: <AddPlots />,
    });
  }

  // Legality routes
  let legalityRoutes = [
    { path: PATH_APP_LEGALITY.root, element: <LegalityPage /> },
  ];

  if (hasFulLAccessToLegality) {
    legalityRoutes.push({
      path: `${PATH_APP_LEGALITY.create}`,
      element: <CreateLegality />,
    });
  }
  if (hasFulLAccessToLegality || hasViewAccessToLegality) {
    legalityRoutes.push(
      {
        path: `${PATH_APP_LEGALITY.companyDocuments}/:companyId/:companyName/`,
        element: <CompanyDocuments />,
      },
      {
        path: `${PATH_APP_LEGALITY.plotDocuments}/:companyId/:companyName/:plotId`,
        element: <PlotDocuments />,
      }
    );
  }

  //Company routes
  let companyRoutes = [
    {
      path: PATH_APP_USER_MANAGEMENT.companiesOverview,
      element: <CompanyManagementPage />,
    },
  ];

  if (hasFullAccessToCompanies) {
    companyRoutes.push(
      {
        path: PATH_APP_USER_MANAGEMENT.newCompany,
        element: <CreateCompany />,
      },
      {
        path: `${PATH_APP_USER_MANAGEMENT.editCompany}/:id`,
        element: <EditCompany />,
      }
    );
  }

  // User routes
  let userRoutes = [
    {
      path: PATH_APP_USER_MANAGEMENT.usersOverview,
      element: <UsersManagementPage />,
    },
  ];

  if (hasFullAccessToUsers) {
    userRoutes.push(
      {
        path: PATH_APP_USER_MANAGEMENT.newUser,
        element: <CreateUser />,
      },
      {
        path: `${PATH_APP_USER_MANAGEMENT.editUser}/:companyId/:id/:parentCompanyId`,
        element: <EditUser />,
      }
    );
  }

  //GeoJson Serbia
  let geoJsonSerbiaRoutes = [];

  if (isFromAdminOrDevCompany) {
    geoJsonSerbiaRoutes.push({
      path: `${PATH_APP_GEO_JSON_SERBIA.root}`,
      element: <ImportCadastralDataSerbia />,
    });
  }

  //Counters
  let countersRoutes = [];

  if (isFromAdminOrDevCompany) {
    countersRoutes.push({
      path: `${PATH_APP_COUNTERS.root}`,
      element: <CountersPage />,
    });
  }

  //DDS
  let ddsRoutes = [];

  if (isLicensedAdmin) {
    ddsRoutes.push(
      {
        path: `${PATH_APP_DDS.root}`,
        element: <DdsPage />,
      },

      {
        path: `${PATH_APP_DDS.submit}`,
        element: <SubmitDds />,
      },
      {
        path: `${PATH_APP_DDS.edit}/:id`,
        element: <EditDds />,
      },
      {
        path: `${PATH_APP_DDS.read}/:id`,
        element: <EditDds />,
      },
      {
        path: `${PATH_APP_DDS.clone}/:id`,
        element: <EditDds />,
      }
    );
  }

  //Rest routes
  const restRoutes = [
    {
      path: `${PATH_APP_ACCOUNT_SETTINGS.root}`,
      element: <AccountSettingsPage />,
    },
    {
      path: `${PATH_APP_UPDATE_PASSWORD.root}`,
      element: <UpdatePasswordPage />,
    },
    {
      path: `${PATH_APP_HELP.root}`,
      element: <HelpPage />,
    },
    { path: PATH_APP_SELECT_COMPANY.root, element: <SelectedCompanyPage /> },
    { path: `*`, element: <Page404 /> },
  ];

  if (!rootCompanyId) {
    //When MyCompany is not selected yet, or if user has logged in but is disabled in every company so he can not procede he will have this routes
    return [
      { path: PATH_APP_SELECT_COMPANY.root, element: <SelectedCompanyPage /> },
      { path: `*`, element: <Page404 /> },
    ];
  }

  const routes = [
    ...(!hasNoAccessToPlots ? plotRoutes : []),
    ...(!hasNoAccessToCompanies ? companyRoutes : []),
    ...(!hasNoAccessToUsers ? userRoutes : []),
    ...(isFromAdminOrDevCompany ? geoJsonSerbiaRoutes : []),
    ...(isFromAdminOrDevCompany ? countersRoutes : []),
    ...(!hasNoAccessToLegality ? legalityRoutes : []),
    ...(isLicensedAdmin ? ddsRoutes : []),
    ...restRoutes,
  ];

  return routes;
}
